// NATIVE
import React, { useEffect, useState } from 'react';

// INSTALLED
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import Avatar from '@mui/material/Avatar';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import MuiDrawer from '@mui/material/Drawer';
import MenuItem from '@mui/material/MenuItem';
import MenuIcon from '@mui/icons-material/Menu';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { useSelector, useDispatch } from 'react-redux';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PersonIcon from '@mui/icons-material/Person';
import LockIcon from '@mui/icons-material/Lock';
import LogoutIcon from '@mui/icons-material/Logout';
import ListItemIcon from '@mui/material/ListItemIcon';
import Divider from '@mui/material/Divider';
import { styled, useTheme, Theme, CSSObject } from '@mui/material/styles';
import MuiAppBar, { AppBarProps as MuiAppBarProps } from '@mui/material/AppBar';
import { useNavigate } from 'react-router-dom';

// CUSTOM
import { startLogOut } from '../../store/auth';
import { openDrawerAction } from '../../store/globals';
import { eachWordUpper, firstLetterUpperCase, storageGetCurrentUser } from '../../commons/utils';
import { getImageStore } from '../../store/files';
import { Badge } from '@mui/material';
import NotificationIcon from '../../components/header/NotificationIcon';
import { ChangePasswordModal } from '../../components/ChangePasswordModal';

const drawerWidth = 320;

const openedMixin = (theme: Theme): CSSObject => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
}));

interface AppBarProps extends MuiAppBarProps {
  open?: boolean;
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})<AppBarProps>(({ theme, open }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: theme.transitions.create(['width', 'margin'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
  }),
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    ...(open && {
      ...openedMixin(theme),
      '& .MuiDrawer-paper': openedMixin(theme),
    }),
    ...(!open && {
      ...closedMixin(theme),
      '& .MuiDrawer-paper': closedMixin(theme),
    }),
  })
);

const StyledBadge = styled(Badge)(({ theme }) => ({
  '& .MuiBadge-badge': {
    backgroundColor: '#44b700',
    color: '#44b700',
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    '&::after': {
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      borderRadius: '50%',
      animation: 'ripple 1.2s infinite ease-in-out',
      border: '1px solid currentColor',
      content: '""',
    },
  },
  '@keyframes ripple': {
    '0%': {
      transform: 'scale(.8)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(2.4)',
      opacity: 0,
    },
  },
}));

export const Header = () => {
  const theme = useTheme();

  // DISPATCH
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // REDUX
  const { drawer }: any = useSelector((state: any) => state.globals);
  const { photoURL }: any = useSelector((state: any) => state.auth);

  // USE STATES
  const [logoUrl, setLogoUrl] = React.useState('');

  const [open, setOpen] = React.useState(false);

  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(null);
  const [openChangePassword, setOpenChangePassword] = React.useState(false);

  const [companyName, setCompanyName] = useState('Qi Hotels');
  const [userInfo, setUserInfo] = useState({ name: '', email: '' });

  useEffect(() => {
    setCompanyTitleName();
  }, []);

  useEffect(() => {
    getLogoImage();
    fetchUserInfo();
  }, [photoURL]);

  const setCompanyTitleName = async () => {
    const { cliente } = await storageGetCurrentUser();
    // console.log('tradenametradenametradename')
    // console.log(cliente)
    setCompanyName(firstLetterUpperCase(cliente.tradename));
  };

  const fetchUserInfo = async () => {
    const user = await storageGetCurrentUser();
    if (user) {
      setUserInfo({
        name: user.fullName || user.displayName || user.name || 'Usuario',
        email: user.email || '',
      });
    }
  };

  useEffect(() => {
    setOpen(drawer);
  }, [drawer]);

  const getLogoImage = async () => {
    const { photoURL } = await storageGetCurrentUser();

    const response = await dispatch(getImageStore(photoURL, 'image-employee'));

    if (response instanceof Blob) {
      const imageUrl = URL.createObjectURL(response);
      setLogoUrl(imageUrl);
    }
  };

  const handleDrawerOpen = async () => {
    await dispatch(openDrawerAction());
  };

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const closeSession = async () => {
    await dispatch(startLogOut());
    window.location.href = '/';
  };

  const settings = [
    {
      text: 'Perfil',
      icon: <PersonIcon fontSize="small" />,
      action: () => {
        navigate('/perfil');
        handleCloseUserMenu();
      },
    },
    {
      text: 'Cambiar contraseña',
      icon: <LockIcon fontSize="small" />,
      action: () => {
        setOpenChangePassword(true);
        handleCloseUserMenu();
      },
    },
    {
      text: 'Cerrar sesión',
      icon: <LogoutIcon fontSize="small" />,
      action: closeSession,
    },
  ];

  return (
    // <CssBaseline />
    <AppBar position="fixed" open={open}>
      <Toolbar>
        <IconButton
          color="inherit"
          aria-label="open drawer"
          onClick={handleDrawerOpen}
          edge="start"
          sx={{
            marginRight: 5,
            ...(open && { display: 'none' }),
          }}
        >
          <MenuIcon />
        </IconButton>
        <Typography variant="h6" noWrap component="div">
          {companyName?.toUpperCase()}
        </Typography>

        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
          <Box sx={{ px: 2, textAlign: 'right', display: { xs: 'none', md: 'block' } }}>
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 600, color: 'white', lineHeight: 1.1 }}
            >
              {eachWordUpper(userInfo.name)}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: 'rgba(255, 255, 255, 0.8)', fontSize: '0.7rem' }}
            >
              {userInfo.email}
            </Typography>
          </Box>

          <NotificationIcon />

          <Tooltip title="Opciones de usuario">
            <IconButton onClick={handleOpenUserMenu} sx={{ p: 0, ml: 1 }}>
              <StyledBadge
                overlap="circular"
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                variant="dot"
              >
                <Avatar alt={'Q'} src={logoUrl} />
              </StyledBadge>

              <ArrowDropDownIcon sx={{ color: 'white' }} />
            </IconButton>
          </Tooltip>
          <Menu
            sx={{ mt: '45px' }}
            id="menu-appbar"
            anchorEl={anchorElUser}
            anchorOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorElUser)}
            onClose={handleCloseUserMenu}
            PaperProps={{
              elevation: 0,
              sx: {
                overflow: 'visible',
                filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                mt: 1.5,
                borderRadius: '12px',
                minWidth: '200px',
                '& .MuiAvatar-root': {
                  width: 32,
                  height: 32,
                  ml: -0.5,
                  mr: 1,
                },
                '&:before': {
                  content: '""',
                  display: 'block',
                  position: 'absolute',
                  top: 0,
                  right: 14,
                  width: 10,
                  height: 10,
                  bgcolor: 'background.paper',
                  transform: 'translateY(-50%) rotate(45deg)',
                  zIndex: 0,
                },
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}
              >
                {userInfo.name}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.8rem' }}>
                {userInfo.email}
              </Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            {settings.map((setting) => (
              <MenuItem
                key={setting.text}
                onClick={setting.action}
                sx={{
                  py: 1.2,
                  px: 2,
                  borderRadius: '8px',
                  mx: 1,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                  },
                }}
              >
                <ListItemIcon sx={{ minWidth: '36px !important', color: 'primary.main' }}>
                  {setting.icon}
                </ListItemIcon>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {setting.text}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>
      </Toolbar>
      <ChangePasswordModal open={openChangePassword} onClose={() => setOpenChangePassword(false)} />
    </AppBar>
  );
};
